/**
 * 💪 Salud — hábitos, macros y entrenamientos con UI visual.
 */
import { h, apiClient, btn, openModal, openConfirm, toast, sectionHeader, statTile, emptyState, openQuickTaskModal } from './_common.js';

export async function renderSalud(root) {
  root.innerHTML = '';
  const wrap = h('div', { class: 'animate-fade-in' });
  wrap.appendChild(sectionHeader('💪', 'Salud', 'Fitness · Nutrición · Hábitos', [
    btn('✅ + Tarea', () => openQuickTaskModal('salud'), { variant: 'secondary' }),
    btn('+ Hábito', () => openHabitModal(null, () => renderSalud(root)), { variant: 'secondary' }),
    btn('+ Entreno', () => openWorkoutModal(null, () => renderSalud(root))),
  ]));

  // Stats bar
  const statsBar = h('div', { id: 'salud-stats', class: 'grid grid-cols-2 md:grid-cols-4 gap-3 mb-6' });
  wrap.appendChild(statsBar);
  const coachCol = h('div', { class: 'mb-6' });
  wrap.appendChild(coachCol);

  // Grid principal
  wrap.appendChild(h('div', { class: 'grid grid-cols-1 lg:grid-cols-2 gap-4' }, [
    h('div', { id: 'habits-col' }),
    h('div', { id: 'workouts-col' }),
  ]));
  root.appendChild(wrap);

  // Cargar datos
  await Promise.all([
    renderCoach(coachCol),
    loadHabits(wrap, root),
    loadWorkouts(wrap, root),
  ]);
}

async function loadHabits(wrap, root) {
  const col = wrap.querySelector('#habits-col');
  col.innerHTML = '';
  const card = h('section', { class: 'bg-base-800/50 border border-base-700 rounded-xl p-5' }, [
    h('div', { class: 'flex items-center justify-between mb-3' }, [
      h('h3', { class: 'font-display font-semibold' }, '🔥 Hábitos'),
      h('button', { class: 'text-gold-300 hover:text-gold-200 text-xl', onclick: () => openHabitModal(null, () => renderSalud(root)) }, '+'),
    ]),
    h('div', { id: 'habits-body', class: 'space-y-2' }, h('div', { class: 'text-ink-400 text-sm' }, 'Cargando...')),
  ]);
  col.appendChild(card);
  const r = await apiClient.get('/api/v1/habits').catch(() => ({ data: [] }));
  const body = card.querySelector('#habits-body');
  body.innerHTML = '';
  if (!r.data || r.data.length === 0) {
    body.appendChild(h('p', { class: 'text-sm text-ink-300 text-center py-4' }, 'Sin hábitos todavía'));
    return;
  }
  r.data.forEach((hb) => body.appendChild(h('div', {
    class: 'flex items-center justify-between bg-base-700/40 hover:bg-base-700/70 rounded-lg p-3 transition cursor-pointer',
    onclick: async () => {
      try {
        await apiClient.post(`/api/v1/habits/${hb.id}/check`, {});
        toast('Hábito marcado', 'success');
        loadHabits(wrap, root);
      } catch { toast('No se pudo registrar', 'error'); }
    },
  }, [
    h('span', { class: 'font-medium' }, `${hb.emoji || '•'} ${hb.name}`),
    h('span', { class: 'text-sm text-gold-300 font-semibold' }, `${hb.streakDays || 0}🔥`),
  ])));
  // Update stats
  updateStats(wrap, { habits: r.data.length });
}

async function loadMacros(wrap) {
  const col = wrap.querySelector('#macros-col');
  col.innerHTML = '';
  const card = h('section', { class: 'bg-base-800/50 border border-base-700 rounded-xl p-5' }, [
    h('h3', { class: 'font-display font-semibold mb-3' }, '🍽️ Macros hoy'),
    h('div', { id: 'macros-body' }),
  ]);
  col.appendChild(card);
  const r = await apiClient.get('/api/v1/macros/today').catch(() => null);
  const body = card.querySelector('#macros-body');
  if (!r || !r.goal) {
    body.innerHTML = '';
    body.appendChild(h('p', { class: 'text-sm text-ink-300 text-center py-4' }, 'Configura tus objetivos macro'));
    body.appendChild(h('div', { class: 'mt-2 text-center' }, btn('Configurar', () => openMacroGoalsModal(() => loadMacros(wrap)), { variant: 'secondary' })));
    return;
  }
  const macros = [
    { k: 'calories', label: 'Kcal', cur: r.consumed?.calories || 0, goal: r.goal.calories || 0, color: 'gold' },
    { k: 'protein',  label: 'Proteína (g)', cur: r.consumed?.protein || 0, goal: r.goal.protein || 0, color: 'red' },
    { k: 'carbs',    label: 'Carbos (g)', cur: r.consumed?.carbs || 0, goal: r.goal.carbs || 0, color: 'blue' },
    { k: 'fat',      label: 'Grasas (g)', cur: r.consumed?.fat || 0, goal: r.goal.fat || 0, color: 'purple' },
  ];
  body.innerHTML = '';
  macros.forEach((m) => {
    const pct = Math.min(100, Math.round((m.cur / Math.max(1, m.goal)) * 100));
    body.appendChild(h('div', { class: 'mb-3' }, [
      h('div', { class: 'flex justify-between text-xs mb-1' }, [h('span', {}, m.label), h('span', { class: 'text-ink-300' }, `${m.cur} / ${m.goal}`)]),
      h('div', { class: 'h-2 bg-base-700 rounded-full overflow-hidden' }, [
        h('div', { class: `h-full bg-${m.color}-400 transition-all`, style: `width: ${pct}%` }),
      ]),
    ]));
  });
}

async function loadWorkouts(wrap, root) {
  const col = wrap.querySelector('#workouts-col');
  col.innerHTML = '';
  const card = h('section', { class: 'bg-base-800/50 border border-base-700 rounded-xl p-5' }, [
    h('div', { class: 'flex items-center justify-between mb-3' }, [
      h('h3', { class: 'font-display font-semibold' }, '🏋️ Entrenamientos'),
      h('button', { class: 'text-gold-300 hover:text-gold-200 text-xl', onclick: () => openWorkoutModal(null, () => renderSalud(root)) }, '+'),
    ]),
    h('div', { id: 'workouts-body', class: 'space-y-2' }),
  ]);
  col.appendChild(card);
  const r = await apiClient.get('/api/v1/workouts/templates').catch(() => ({ data: [] }));
  const body = card.querySelector('#workouts-body');
  body.innerHTML = '';
  if (!r.data || r.data.length === 0) {
    body.appendChild(h('p', { class: 'text-sm text-ink-300 text-center py-4' }, 'Sin plantillas de entreno'));
    return;
  }
  r.data.forEach((w) => body.appendChild(h('div', {
    class: 'bg-base-700/40 hover:bg-base-700/70 rounded-lg p-3 transition cursor-pointer text-sm',
  }, [
    h('div', { class: 'font-medium' }, w.name),
    w.muscleGroups ? h('div', { class: 'text-xs text-ink-400 mt-0.5' }, (w.muscleGroups || []).join(', ')) : null,
  ])));
}

function updateStats(wrap, partial) {
  const bar = wrap.querySelector('#salud-stats');
  if (!bar) return;
  bar.innerHTML = '';
  bar.appendChild(statTile({ label: 'Hábitos activos', value: partial.habits ?? '—', emoji: '🔥', color: 'gold' }));
  bar.appendChild(statTile({ label: 'Días seguidos', value: '0', emoji: '📅', color: 'green' }));
  bar.appendChild(statTile({ label: 'Entrenos esta semana', value: '0', emoji: '🏋️', color: 'blue' }));
  bar.appendChild(statTile({ label: 'Kcal hoy', value: '—', emoji: '🍽️', color: 'purple' }));
}

function openHabitModal(habit, onDone) {
  openModal({
    title: habit ? 'Editar hábito' : 'Nuevo hábito',
    icon: '🔥',
    fields: [
      { name: 'name', label: 'Nombre', required: true, value: habit?.name, placeholder: 'p.ej. Meditar 10 min' },
      { name: 'emoji', label: 'Emoji', value: habit?.emoji || '💪', placeholder: '💪' },
      { name: 'targetFrequency', label: 'Frecuencia', type: 'select', value: habit?.targetFrequency || 'daily', options: [
        { value: 'daily', label: 'Diario' },
        { value: 'weekly', label: 'Semanal' },
        { value: 'custom', label: 'Personalizado' },
      ] },
    ],
    onSubmit: async (vals) => {
      if (habit?.id) {
        await apiClient.patch(`/api/v1/habits/${habit.id}`, vals);
        toast('Hábito actualizado', 'success');
      } else {
        await apiClient.post('/api/v1/habits', vals);
        toast('Hábito creado', 'success');
      }
      onDone();
    },
  });
}

function openWorkoutModal(workout, onDone) {
  openModal({
    title: workout ? 'Editar entreno' : 'Nuevo entreno',
    icon: '🏋️',
    size: 'lg',
    fields: [
      { name: 'name', label: 'Nombre', required: true, value: workout?.name, placeholder: 'p.ej. Push Day' },
      { name: 'description', label: 'Descripción', type: 'textarea', rows: 3, value: workout?.description },
      { name: 'estimatedDurationMin', label: 'Duración (min)', type: 'number', value: workout?.estimatedDurationMin ?? 60 },
    ],
    onSubmit: async (vals) => {
      const payload = { ...vals, estimatedDurationMin: Number(vals.estimatedDurationMin) || 60 };
      if (workout?.id) {
        await apiClient.patch(`/api/v1/workouts/templates/${workout.id}`, payload);
        toast('Plantilla actualizada', 'success');
      } else {
        await apiClient.post('/api/v1/workouts/templates', payload);
        toast('Plantilla creada', 'success');
      }
      onDone();
    },
  });
}

function openMacroGoalsModal(onDone) {
  openModal({
    title: 'Objetivos macro',
    icon: '🍽️',
    fields: [
      { name: 'calories', label: 'Calorías diarias', type: 'number', value: 2500, required: true },
      { name: 'protein', label: 'Proteína (g)', type: 'number', value: 180 },
      { name: 'carbs', label: 'Carbohidratos (g)', type: 'number', value: 250 },
      { name: 'fat', label: 'Grasas (g)', type: 'number', value: 80 },
    ],
    onSubmit: async (vals) => {
      await apiClient.post('/api/v1/macros/goals', {
        calories: Number(vals.calories) || 0,
        protein: Number(vals.protein) || 0,
        carbs: Number(vals.carbs) || 0,
        fat: Number(vals.fat) || 0,
      });
      toast('Objetivos guardados', 'success');
      onDone();
    },
  });
}

// ====================== COACH FITNESS ======================
async function renderCoach(root){
  const SS='background:#0f1830;border:1px solid #2a3a5c;border-radius:8px;color:#e8eef7;font-size:13px;padding:8px 10px;width:100%';
  const sel=()=>({style:SS});
  const inp=(ph)=>({type:'number',inputmode:'decimal',placeholder:ph,style:SS});
  const opt=(v,l)=>h('option',{value:v},l);
  const field=(label,el)=>h('div',{class:'mb-2'},[ h('div',{class:'text-[11px] text-ink-400 mb-1'},label), el ]);
  const goldBtn=(t)=>h('button',{class:'text-sm font-bold px-4 py-2 rounded-lg w-full',style:'background:linear-gradient(90deg,#EAB546,#E8A020);color:#1b1304'},t);
  const badge=(t,c)=>h('span',{class:'inline-flex items-center text-xs px-2.5 py-1 rounded-full',style:`background:${c||'#26365A'}33;border:1px solid ${c||'#33446A'}66;color:#e8eef7`},t);
  const localToday=()=>{const d=new Date();return d.getFullYear()+'-'+String(d.getMonth()+1).padStart(2,'0')+'-'+String(d.getDate()).padStart(2,'0');};
  const TIPS={perder:'Prioriza proteina y un deficit moderado. Fuerza 3-4x/semana + pasos diarios. Pesate en ayunas, mismo dia/semana.',mantener:'Mantén la proteina alta y entrena fuerza para conservar musculo. Ajusta segun la balanza.',ganar:'Superavit ligero, proteina alta y sobrecarga progresiva. Si subes >0.5kg/sem baja un poco las kcal.'};

  root.innerHTML='';
  const card=h('section',{class:'border border-base-700 rounded-2xl p-5',style:'background:linear-gradient(135deg,rgba(232,160,32,.07),rgba(232,160,32,0))'});
  card.appendChild(h('div',{class:'flex items-center gap-3 mb-4'},[
    h('div',{class:'w-11 h-11 rounded-2xl flex items-center justify-center text-2xl',style:'background:linear-gradient(135deg,rgba(232,160,32,.25),rgba(232,160,32,.05));border:1px solid rgba(232,160,32,.35)'},'🏋️'),
    h('div',{},[ h('h3',{class:'font-display text-xl font-bold'},'Coach Fitness'), h('p',{class:'text-ink-300 text-sm'},'Rellena 4 datos y te calculo tus calorias y macros al instante.') ]),
  ]));
  const body=h('div',{}); card.appendChild(body); root.appendChild(card);

  async function refresh(){
    body.innerHTML='<div class="text-ink-400 text-sm py-3">Cargando…</div>';
    const d=await apiClient.get('/api/v1/coach').catch(()=>({perfil:null,pesos:[],macros:null}));
    body.innerHTML=''; body.appendChild(build(d));
  }
  let semanaReload=null;
  function comidasCard(d){
    const m=d.macros;
    const shiftDay=(ff,n)=>{const a=ff.split('-').map(Number);const dt=new Date(a[0],a[1]-1,a[2]+n);return dt.getFullYear()+'-'+String(dt.getMonth()+1).padStart(2,'0')+'-'+String(dt.getDate()).padStart(2,'0');};
    let dia=(d.hoy&&d.hoy.fecha)||localToday();
    const card=h('section',{class:'border border-base-700 rounded-2xl p-5 mt-4',style:'background:linear-gradient(135deg,rgba(56,189,248,.06),transparent)'});
    const titleDate=h('span',{class:'text-ink-300 text-xs font-semibold'});
    const inner=h('div',{});
    const esHoy=()=>dia===localToday();
    card.appendChild(h('div',{class:'flex items-center justify-between mb-3'},[
      h('h3',{class:'font-display font-bold text-lg'},'🍽️ Comidas'),
      h('div',{class:'flex items-center gap-2'},[
        h('button',{class:'w-8 h-8 rounded-lg bg-base-700 border border-base-600',onclick:()=>{ dia=shiftDay(dia,-1); load(); }},'‹'),
        titleDate,
        h('button',{class:'w-8 h-8 rounded-lg bg-base-700 border border-base-600',style:'opacity:.9',onclick:()=>{ if(!esHoy()){ dia=shiftDay(dia,1); load(); } }},'›'),
      ]),
    ]));
    card.appendChild(inner);
    async function load(){
      const data=await apiClient.get('/api/v1/coach/comidas?fecha='+dia).catch(()=>({comidas:[],consumido:{kcal:0,protein_g:0,carbs_g:0,fat_g:0}}));
      paint(data);
    }
    function paint(data){
      titleDate.textContent=esHoy()?('Hoy · '+dia):dia;
      const con=data.consumido||{kcal:0,protein_g:0,carbs_g:0,fat_g:0};
      inner.innerHTML='';
      const bar=(label,cur,goal,color)=>{ const pct=goal?Math.min(100,Math.round(cur/goal*100)):0; const rem=goal?Math.max(0,Math.round(goal-cur)):null; return h('div',{class:'mb-2'},[ h('div',{class:'flex justify-between text-xs mb-1'},[ h('span',{},label), h('span',{class:'text-ink-300'}, Math.round(cur)+(goal?(' / '+goal+(rem!=null?(' · faltan '+rem):'')):'')) ]), h('div',{class:'h-2.5 rounded-full overflow-hidden',style:'background:#1b2742'},[ h('div',{style:'height:100%;transition:.3s;width:'+pct+'%;background:'+color}) ]) ]); };
      const tgt=data.target||(m?{kcal:m.kcal,protein_g:m.protein_g,carbs_g:m.carbs_g,fat_g:m.fat_g}:null);
      inner.appendChild(h('div',{class:'mb-4'},[ bar('Calorias',con.kcal,tgt?tgt.kcal:0,'#EAB546'), bar('Proteina',con.protein_g,tgt?tgt.protein_g:0,'#34D399'), bar('Carbos',con.carbs_g,tgt?tgt.carbs_g:0,'#38BDF8'), bar('Grasa',con.fat_g,tgt?tgt.fat_g:0,'#FB7185') ]));
      const desc=h('input',{type:'text',placeholder:'Ej: 200g pechuga de pollo y 100g de arroz',style:SS});
      const ai=h('button',{class:'text-sm font-bold px-3 py-2 rounded-lg whitespace-nowrap flex-1 sm:flex-none',style:'background:linear-gradient(90deg,#38BDF8,#0ea5e9);color:#04263a'},'✨ Estimar IA');
      const fileIn=h('input',{type:'file',accept:'image/*',style:'display:none'});
      const foto=h('button',{class:'text-sm font-bold px-3 py-2 rounded-lg whitespace-nowrap flex-1 sm:flex-none',style:'background:#26365A;border:1px solid #33446A;color:#CBD5E1'},'📷 Foto');
      foto.onclick=()=>fileIn.click();
      fileIn.onchange=async()=>{ const fl=fileIn.files&&fileIn.files[0]; if(!fl) return; foto.textContent='Analizando…';
        try{ const dataUrl=await new Promise((rs,rj)=>{ const img=new Image(); img.onload=()=>{ let w=img.width,hh=img.height; const mx=768; if(w>hh&&w>mx){hh=Math.round(hh*mx/w);w=mx;}else if(hh>mx){w=Math.round(w*mx/hh);hh=mx;} const cv=document.createElement('canvas'); cv.width=w; cv.height=hh; cv.getContext('2d').drawImage(img,0,0,w,hh); rs(cv.toDataURL('image/jpeg',0.7)); }; img.onerror=rj; const fr=new FileReader(); fr.onload=()=>{img.src=fr.result;}; fr.readAsDataURL(fl); });
          const b64=String(dataUrl).split(',')[1];
          const r=await apiClient.post('/api/v1/coach/estimar-foto',{imagen:b64,media_type:'image/jpeg'}).catch(()=>null);
          foto.textContent='📷 Foto'; fileIn.value='';
          if(r&&r.ok){ showForm({nombre:r.nombre,kcal:r.kcal,protein_g:r.protein_g,carbs_g:r.carbs_g,fat_g:r.fat_g,detalle:r.detalle}); } else { showForm({nombre:'Plato (foto)'}); }
        }catch(e){ foto.textContent='📷 Foto'; }
      };
      const formWrap=h('div',{class:'mt-2'});
      function showForm(v,editId){ formWrap.innerHTML=''; const n=h('input',{type:'text',value:v.nombre||'',placeholder:'Nombre',style:SS}); const kc=h('input',inp('kcal')); kc.value=v.kcal!=null?v.kcal:''; const pp=h('input',inp('Prot g')); pp.value=v.protein_g!=null?v.protein_g:''; const cc=h('input',inp('Carb g')); cc.value=v.carbs_g!=null?v.carbs_g:''; const gg=h('input',inp('Gras g')); gg.value=v.fat_g!=null?v.fat_g:''; const add=h('button',{class:'text-sm font-bold px-4 py-2 rounded-lg',style:'background:linear-gradient(90deg,#EAB546,#E8A020);color:#1b1304'}, editId?'Guardar cambios':'Anadir comida'); add.onclick=async()=>{ add.textContent='...'; const payload={fecha:dia,nombre:n.value||'Comida',kcal:kc.value,protein_g:pp.value,carbs_g:cc.value,fat_g:gg.value}; if(editId){ await apiClient.patch('/api/v1/coach/comida/'+editId,payload).catch(()=>{}); } else { await apiClient.post('/api/v1/coach/comida',payload).catch(()=>{}); } await load(); if(semanaReload) semanaReload(); }; formWrap.appendChild(h('div',{class:'bg-base-900 rounded-xl p-3'},[ h('div',{class:'text-[11px] text-ink-400 mb-2'}, editId?'Edita y guarda:':'Revisa y ajusta, luego anade:'), n, (v.detalle? h('div',{class:'text-[11px] mb-2',style:'color:#9fb0cc;line-height:1.4'}, '🔎 '+v.detalle):null), h('div',{class:'grid grid-cols-2 sm:grid-cols-4 gap-2 mt-2'},[ kc, pp, cc, gg ]), h('div',{class:'flex justify-end mt-2'},[ add ]) ])); }
      ai.onclick=async()=>{ const t=desc.value.trim(); if(!t) return; ai.textContent='Pensando...'; const r=await apiClient.post('/api/v1/coach/estimar',{texto:t}).catch(()=>null); ai.textContent='✨ Estimar IA'; if(r&&r.ok){ showForm({nombre:r.nombre,kcal:r.kcal,protein_g:r.protein_g,carbs_g:r.carbs_g,fat_g:r.fat_g,detalle:r.detalle}); } else { showForm({nombre:t}); } };
      inner.appendChild(h('div',{class:'flex flex-col sm:flex-row gap-2'},[ desc, h('div',{class:'flex gap-2'},[ ai, foto ]) ]));
      inner.appendChild(fileIn);
      inner.appendChild(h('button',{class:'text-xs text-ink-300 underline mt-1',onclick:()=>showForm({})},'+ Anadir manualmente'));
      inner.appendChild(formWrap);
      const list=h('div',{class:'mt-4 space-y-1'});
      const comidas=data.comidas||[];
      if(!comidas.length){ list.appendChild(h('div',{class:'text-xs text-ink-400'},'Sin comidas este dia.')); }
      comidas.forEach(c=> list.appendChild(h('div',{class:'flex items-center justify-between bg-base-900 rounded-lg px-3 py-2'},[ h('div',{class:'min-w-0'},[ h('div',{class:'text-sm font-medium truncate'}, c.nombre), h('div',{class:'text-[11px] text-ink-400'}, Math.round(c.kcal)+' kcal · '+Math.round(c.protein_g)+'P '+Math.round(c.carbs_g)+'C '+Math.round(c.fat_g)+'G') ]), h('div',{class:'flex items-center gap-2 flex-shrink-0'},[ h('button',{class:'w-9 h-9 rounded-lg bg-base-700 flex items-center justify-center',title:'Editar',onclick:()=>showForm({nombre:c.nombre,kcal:c.kcal,protein_g:c.protein_g,carbs_g:c.carbs_g,fat_g:c.fat_g},c.id)},'✏️'), h('button',{class:'w-9 h-9 rounded-lg bg-base-700 flex items-center justify-center',title:'Borrar',onclick:async()=>{ await apiClient.delete('/api/v1/coach/comida/'+c.id).catch(()=>{}); await load(); if(semanaReload) semanaReload(); }},'🗑️') ]) ])));
      inner.appendChild(list);
    }
    load();
    return card;
  }
  function semanaCard(){
    const card=h('section',{class:'border border-base-700 rounded-2xl p-5 mt-4'});
    card.appendChild(h('div',{class:'flex items-center justify-between mb-3'},[ h('h3',{class:'font-display font-bold text-lg'},'📈 Resumen semanal'), h('span',{class:'text-ink-400 text-xs'},'Ultimos 7 dias') ]));
    const bodyEl=h('div',{}); card.appendChild(bodyEl);
    async function reload(){
      const d=await apiClient.get('/api/v1/coach/semana').catch(()=>null);
      bodyEl.innerHTML='';
      if(!d){ bodyEl.appendChild(h('div',{class:'text-xs text-ink-400'},'No disponible.')); return; }
      const canvas=h('canvas');
      bodyEl.appendChild(h('div',{style:'height:170px'},[canvas]));
      const target=d.target||0;
      const labels=d.serie.map(x=>x.fecha.slice(5));
      bodyEl.appendChild(h('div',{class:'flex flex-wrap gap-2 mt-3 text-xs'},[
        h('span',{class:'inline-flex items-center px-2.5 py-1 rounded-full',style:'background:#26365A33;border:1px solid #33446A66;color:#e8eef7'},'Registrados: '+d.dias_registrados+'/7'),
        h('span',{class:'inline-flex items-center px-2.5 py-1 rounded-full',style:'background:#26365A33;border:1px solid #33446A66;color:#e8eef7'},'Media: '+d.media_kcal+' kcal'),
        target? h('span',{class:'inline-flex items-center px-2.5 py-1 rounded-full',style:'background:#34D39922;border:1px solid #34D39955;color:#a7f3d0'},'En objetivo: '+d.dias_en_objetivo+' dias') : null,
      ]));
      setTimeout(()=>{ try{ if(window.Chart){ const ds=[{type:'bar',label:'kcal',data:d.serie.map(x=>x.kcal),backgroundColor:d.serie.map(x=> (target&&Math.abs(x.kcal-target)<=target*0.15)?'#34D399':'#EAB546'),borderRadius:6}]; if(target){ ds.push({type:'line',label:'objetivo',data:d.serie.map(()=>target),borderColor:'#38BDF8',borderDash:[5,5],pointRadius:0,borderWidth:2}); } const cfg={data:{labels:labels,datasets:ds},options:{responsive:true,maintainAspectRatio:false,plugins:{legend:{display:false}},scales:{y:{beginAtZero:true,ticks:{color:'#93a4bc'},grid:{color:'rgba(255,255,255,.05)'}},x:{ticks:{color:'#93a4bc'},grid:{display:false}}}}}; new window.Chart(canvas,cfg); } }catch(e){} },60);
    }
    semanaReload=reload; reload();
    return card;
  }
  function medidasCard(d){
    const meds=d.medidas||[];
    const last=meds.length?meds[meds.length-1]:null;
    const parts=[['Cintura','cintura_cm'],['Pecho','pecho_cm'],['Cadera','cadera_cm'],['Brazo der.','brazo_der_cm'],['Brazo izq.','brazo_izq_cm'],['Brazo contr.','brazo_contraido_cm'],['Muslo','muslo_cm'],['Cuello','cuello_cm']];
    const card=h('section',{class:'border border-base-700 rounded-2xl p-5 mt-4',style:'background:linear-gradient(135deg,rgba(52,211,153,.06),transparent)'});
    card.appendChild(h('div',{class:'flex items-center justify-between mb-3'},[
      h('h3',{class:'font-display font-bold text-lg'},'📏 Medidas (cm)'),
      h('span',{class:'text-ink-400 text-xs'}, last?('Última: '+last.fecha):'Sin registros'),
    ]));
    if(last){
      card.appendChild(h('div',{class:'grid grid-cols-2 sm:grid-cols-4 gap-2 mb-3'}, parts.filter(function(x){return last[x[1]]!=null;}).map(function(x){ return h('div',{class:'rounded-xl p-2 text-center',style:'background:#26365A22;border:1px solid #33446A55'},[ h('div',{class:'text-[10px] text-ink-400'},x[0]), h('div',{class:'font-display font-extrabold text-lg',style:'color:#34D399'}, last[x[1]]+' cm') ]); })));
    } else {
      card.appendChild(h('div',{class:'text-xs text-ink-400 mb-3'},'Aún no has registrado medidas. Añádelas abajo.'));
    }
    const fields={};
    const gridInputs=h('div',{class:'grid grid-cols-2 sm:grid-cols-4 gap-2'}, parts.map(function(x){ const el=h('input',inp(x[0])); if(last&&last[x[1]]!=null){ el.value=last[x[1]]; el.placeholder=last[x[1]]+' cm'; } fields[x[1]]=el; return field(x[0],el); }));
    const save=goldBtn('Guardar medidas de hoy');
    save.onclick=async function(){ const payload={fecha:localToday()}; let any=false; parts.forEach(function(x){ const v=String(fields[x[1]].value||'').trim(); if(v){ payload[x[1]]=v; any=true; } }); if(!any){ try{toast('Escribe al menos una medida','error');}catch(e){} return; } save.textContent='…'; await apiClient.post('/api/v1/coach/medida',payload).catch(function(){}); await refresh(); };
    card.appendChild(h('div',{class:'bg-base-900 rounded-xl p-3'},[ h('div',{class:'text-[11px] text-ink-400 mb-2'},'Registrar medidas (deja en blanco lo que no midas):'), gridInputs, h('div',{class:'flex justify-end mt-2'},[save]) ]));
    if(meds.length){
      const hist=h('div',{class:'space-y-1 mt-3'}, meds.slice(-6).reverse().map(function(x){ const resumen=parts.filter(function(pp){return x[pp[1]]!=null;}).map(function(pp){ return pp[0].split(' ')[0].toLowerCase()+' '+x[pp[1]]; }).join(' · '); return h('div',{class:'flex items-center justify-between text-xs bg-base-900 rounded-lg px-2.5 py-1.5'},[ h('span',{class:'text-ink-300'}, x.fecha), h('span',{class:'flex items-center gap-2'},[ h('span',{class:'text-ink-200 truncate',style:'max-width:55vw'}, resumen), h('button',{title:'Borrar',onclick:async function(){ if(confirm('¿Borrar estas medidas?')){ await apiClient.delete('/api/v1/coach/medida/'+x.id).catch(function(){}); await refresh(); } }},'🗑️') ]) ]); }));
      card.appendChild(hist);
    }
    return card;
  }
  function build(d){
    const p=d.perfil||{};
    // --- PERFIL ---
    const sexo=h('select',sel(),[opt('M','Hombre'),opt('F','Mujer')]); sexo.value=p.sexo||'M';
    const edad=h('input',inp('años')); edad.value=p.edad!=null?p.edad:'';
    const altura=h('input',inp('cm')); altura.value=p.altura_cm!=null?p.altura_cm:'';
    const act=h('select',sel(),[opt('sedentario','Sedentario'),opt('ligero','Ligero (1-2 dias)'),opt('moderado','Moderado (3-4)'),opt('activo','Activo (5-6)'),opt('muy_activo','Muy activo')]); act.value=p.actividad||'moderado';
    const obj=h('select',sel(),[opt('perder','Perder grasa'),opt('mantener','Mantener'),opt('ganar','Ganar musculo')]); obj.value=p.objetivo||'mantener';
    const ritmo=h('select',sel(),[opt('suave','Suave'),opt('normal','Normal'),opt('agresivo','Agresivo')]); ritmo.value=p.ritmo||'normal';
    const sp=goldBtn('✨ Calcular y fijar macros');
    sp.onclick=async()=>{ sp.textContent='Guardando…'; await apiClient.put('/api/v1/coach/perfil',{sexo:sexo.value,edad:edad.value,altura_cm:altura.value,actividad:act.value,objetivo:obj.value,ritmo:ritmo.value}).catch(()=>{}); await refresh(); };
    const perfilCard=h('div',{class:'bg-base-800/60 border border-base-700 rounded-xl p-4'},[
      h('div',{class:'text-xs font-semibold mb-3',style:'color:#EAB546'},'👤 PERFIL'),
      h('div',{class:'grid grid-cols-2 gap-2'},[ field('Sexo',sexo), field('Edad',edad), field('Altura',altura), field('Actividad',act) ]),
      field('Objetivo',obj), field('Ritmo',ritmo), h('div',{class:'mt-2'},[sp]),
    ]);

    // --- PESO ---
    const pin=h('input',inp('kg')); 
    const reg=goldBtn('Registrar peso');
    reg.onclick=async()=>{ const v=parseFloat(String(pin.value).replace(',','.')); if(!v||v<=0){pin.focus();return;} reg.textContent='…'; await apiClient.post('/api/v1/coach/peso',{peso_kg:v,fecha:localToday()}).catch(()=>{}); pin.value=''; await refresh(); };
    const canvas=h('canvas');
    const pesos=d.pesos||[];
    const head=h('div',{class:'flex items-end justify-between mb-2'},[
      h('div',{},[ h('div',{class:'text-xs font-semibold',style:'color:#EAB546'},'⚖️ PESO'), h('div',{class:'font-display font-extrabold text-2xl'}, (d.peso_actual!=null? d.peso_actual+' kg':'— kg')) ]),
      d.cambio!=null? h('div',{class:'text-sm font-semibold',style:`color:${d.cambio<=0?'#34D399':'#FB7185'}`}, (d.cambio>0?'+':'')+d.cambio+' kg') : null,
    ]);
    const recent=h('div',{class:'space-y-1 mt-2'}, pesos.slice(-5).reverse().map(x=>h('div',{class:'flex items-center justify-between text-xs bg-base-900 rounded-lg px-2.5 py-1.5'},[
      h('span',{class:'text-ink-300'}, x.fecha), h('span',{class:'flex items-center gap-2'},[ h('b',{},x.peso_kg+' kg'), h('button',{title:'Borrar',onclick:async()=>{ if(confirm('¿Borrar este registro?')){ await apiClient.delete('/api/v1/coach/peso/'+x.id).catch(()=>{}); await refresh(); } }},'🗑️') ]),
    ])));
    const pesoCard=h('div',{class:'bg-base-800/60 border border-base-700 rounded-xl p-4'},[
      head,
      h('div',{class:'flex flex-col sm:flex-row gap-2 mb-2'},[ pin, reg ]),
      pesos.length? h('div',{style:'height:150px'},[canvas]) : h('div',{class:'text-xs text-ink-400 py-3'},'Registra tu peso para ver la evolucion.'),
      recent,
    ]);
    if(pesos.length){ setTimeout(()=>{ try{ if(window.Chart) new window.Chart(canvas,{type:'line',data:{labels:pesos.map(x=>x.fecha.slice(5)),datasets:[{data:pesos.map(x=>x.peso_kg),borderColor:'#EAB546',backgroundColor:'rgba(232,160,32,.15)',fill:true,tension:.3,pointRadius:3,borderWidth:2}]},options:{responsive:true,maintainAspectRatio:false,plugins:{legend:{display:false}},scales:{y:{ticks:{color:'#93a4bc'},grid:{color:'rgba(255,255,255,.05)'}},x:{ticks:{color:'#93a4bc'},grid:{display:false}}}}}); }catch(e){} },60); }

    // --- MACROS ---
    let resCard;
    const m=d.macros;
    if(m){
      const chip=(label,g,kcal,color)=>h('div',{class:'rounded-xl p-3 text-center',style:`background:${color}22;border:1px solid ${color}55`},[
        h('div',{class:'text-[11px] text-ink-300'},label), h('div',{class:'font-display font-extrabold text-lg',style:`color:${color}`}, g+' g'), h('div',{class:'text-[10px] text-ink-400'}, kcal+' kcal'),
      ]);
      resCard=h('div',{class:'bg-base-800/60 border border-base-700 rounded-xl p-4'},[
        h('div',{class:'text-xs font-semibold mb-2',style:'color:#EAB546'},'🎯 TUS MACROS / DIA'),
        h('div',{class:'font-display font-extrabold text-3xl',style:'color:#EAB546'}, m.kcal+' kcal'),
        h('div',{class:'text-[11px] text-ink-400 mb-3'}, 'BMR '+m.bmr+' · TDEE '+m.tdee+' · '+(m.ajuste>=0?'+':'')+m.ajuste+' kcal'),
        h('div',{class:'grid grid-cols-3 gap-2 mb-3'},[ chip('Proteina',m.protein_g,m.protein_g*4,'#34D399'), chip('Carbos',m.carbs_g,m.carbs_g*4,'#38BDF8'), chip('Grasa',m.fat_g,m.fat_g*9,'#FB7185') ]),
        h('div',{class:'flex flex-wrap gap-2 mb-3'},[ badge('IMC '+m.imc+' · '+m.imc_cat,'#EAB546'), badge('💧 '+m.agua_l+' L/dia','#38BDF8') ]),
        h('div',{class:'text-xs text-ink-300',style:'line-height:1.5'}, '💡 '+(TIPS[p.objetivo||'mantener']||'')),
        h('div',{class:'text-[10px] text-ink-500 mt-2'}, '🔒 Objetivo fijo. Registrar peso no lo cambia; pulsa Calcular y fijar macros para recalcularlo.'),
      ]);
    } else {
      resCard=h('div',{class:'bg-base-800/60 border border-base-700 rounded-xl p-4 flex items-center'},[
        h('div',{class:'text-sm text-ink-300'},'En 30 segundos: pon tu sexo, edad y altura, dale a Calcular y registra tu peso. Te doy al momento tus calorias y macros del dia para tu objetivo. 💪'),
      ]);
    }
    const grid=h('div',{class:'grid grid-cols-1 lg:grid-cols-3 gap-3'},[ perfilCard, pesoCard, resCard ]);
    return h('div',{},[ grid, medidasCard(d), comidasCard(d), semanaCard() ]);
  }
  await refresh();
}
